import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Sparkles, Loader2, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import BusinessCard from './BusinessCard';

// Custom Lionheart Lion Icon
const LionIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    {/* Stylized Lion Head - Lionheart Brand */}
    <path d="M12 2C8.5 2 5.5 4 4 7c-1 2-.5 4.5 1 6.5.5.7 1.2 1.3 2 1.8V18c0 1.1.9 2 2 2h1v2h4v-2h1c1.1 0 2-.9 2-2v-2.7c.8-.5 1.5-1.1 2-1.8 1.5-2 2-4.5 1-6.5C18.5 4 15.5 2 12 2zm-3 9c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm6 0c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm-3 4c-1.1 0-2-.4-2.5-1h5c-.5.6-1.4 1-2.5 1z"/>
    {/* Mane */}
    <path d="M4 7c-.5-1.5 0-3 1-4 .5 1 1.5 2 3 2.5C6 6 4.5 6.5 4 7zm16 0c.5-1.5 0-3-1-4-.5 1-1.5 2-3 2.5 2 .5 3.5 1 4 1.5zM6 4c-1-.5-2-1.5-2-3 1.5.5 2.5 1.5 3 3-.3 0-.7 0-1 0zm12 0c1-.5 2-1.5 2-3-1.5.5-2.5 1.5-3 3 .3 0 .7 0 1 0z" opacity="0.7"/>
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate a unique session ID
const generateSessionId = () => {
  return 'kata-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
};

const KataChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'kata',
      content: "G'day! I'm Kata, your AI Search Concierge. I can help you find the best local businesses across Australia - restaurants, dentists, real estate agents, tradies, you name it! What are you looking for today?",
      businessCards: []
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Fetch suggestions on mount
    fetchSuggestions();
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hotspots/emma/suggestions`);
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = { role: 'user', content: messageText, businessCards: [] };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/hotspots/emma/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId,
        }),
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: 'kata',
        content: data.response || "I'm having trouble understanding. Could you rephrase that?",
        businessCards: data.business_cards || []
      }]);
    } catch (error) {
      console.error('Kata chat error:', error);
      setMessages(prev => [...prev, {
        role: 'kata',
        content: "Sorry, I'm having a bit of trouble connecting. Try using the search bar above to find what you're looking for!",
        businessCards: []
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  return (
    <>
      {/* Chat Button - Lionheart Branded */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 group ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="kata-chat-button"
      >
        <div className="relative">
          <LionIcon className="h-8 w-8 text-white" />
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full animate-pulse border-2 border-white" />
        </div>
        <span className="absolute -top-10 right-0 bg-slate-900 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Ask Kata AI
        </span>
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-90 opacity-0 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30 shadow-lg">
              <img 
                src="/kata-face.jpg" 
                alt="Kata" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="font-bold text-white">Ask Kata</h3>
              <p className="text-xs text-sky-100">AI Search Concierge</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-[350px] overflow-y-auto p-4 space-y-4 bg-slate-50">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}
            >
              {/* Kata's Avatar */}
              {message.role === 'kata' && (
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-slate-200">
                  <img src="/kata-face.jpg" alt="Kata" className="w-full h-full object-cover" />
                </div>
              )}
              <div className={`max-w-[80%] ${message.role === 'user' ? '' : ''}`}>
                <div
                  className={`rounded-2xl px-4 py-2 ${
                    message.role === 'user'
                      ? 'bg-sky-500 text-white rounded-br-md'
                      : 'bg-white text-slate-700 shadow-sm rounded-bl-md border border-slate-100'
                  }`}
                >
                  {message.role === 'kata' && (
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="h-3 w-3 text-sky-500" />
                      <span className="text-xs font-medium text-sky-500">Kata</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{message.content}</p>
                </div>
                
                {/* Business Cards from Kata */}
                {message.role === 'kata' && message.businessCards && message.businessCards.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Gift className="w-3 h-3" />
                      <span>Recommended for you:</span>
                    </div>
                    {message.businessCards.slice(0, 3).map((business, bizIndex) => (
                      <BusinessCard 
                        key={bizIndex} 
                        business={business} 
                        compact={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white text-slate-700 shadow-sm rounded-2xl rounded-bl-md border border-slate-100 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-sky-500" />
                  <span className="text-sm text-slate-500">Kata is thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {messages.length <= 2 && suggestions.length > 0 && (
          <div className="px-4 py-2 bg-white border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-1">
              {suggestions.slice(0, 3).map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="text-xs bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-700 px-2 py-1 rounded-full transition-colors truncate max-w-full"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-3 bg-white border-t border-slate-200">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 text-sm"
              disabled={isLoading}
              data-testid="kata-input"
            />
            <Button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="bg-sky-500 hover:bg-sky-600 px-3"
              data-testid="kata-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-4 py-2 bg-slate-900 text-center">
          <p className="text-xs text-slate-400">
            Powered by <span className="text-white font-medium">LIONHEART</span>
            <span className="text-orange-500 font-medium"> DIGITAL</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default KataChat;
